import { scopedApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { EndpointResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../requestOptions';

export type PreviewEntityResource = 'catalog' | 'order' | 'return' | 'ship_advice' | 'fulfillment';

export interface ValidatedPreviewEntity {
  id: number;
  resource: PreviewEntityResource;
  label: string;
}

export interface ValidatePreviewEntityArgs {
  contextType: string;
  entityId: number;
}

// Maps registered Twig context types to the Astur resource that owns
// `entity_id`. Contexts that don't accept an entity (e.g. subscription_filename)
// have no entry and are not pickable.
const RESOURCE_BY_CONTEXT_TYPE: Record<string, PreviewEntityResource> = {
  product_mapping: 'catalog',
  order_export: 'order',
  order_return_export: 'return',
  ship_advice_export: 'ship_advice',
  fulfillment_export: 'fulfillment',
};

export function getEntityResourceForContext(contextType: string): PreviewEntityResource | null {
  if (!contextType) return null;
  return RESOURCE_BY_CONTEXT_TYPE[contextType.trim()] ?? null;
}

export function contextTypeAcceptsEntity(contextType: string): boolean {
  return getEntityResourceForContext(contextType) !== null;
}

export function deriveEntityLabel(resource: PreviewEntityResource, raw: Record<string, unknown> | null, fallbackId: number): string {
  if (!raw) {
    return labelForResource(resource, fallbackId);
  }
  if (resource === 'catalog') {
    const name = typeof raw.name === 'string' ? raw.name.trim() : '';
    const sku = typeof raw.sku === 'string' ? raw.sku.trim() : '';
    if (name && sku) return `${name} (${sku})`;
    if (name) return name;
    if (sku) return sku;
    return `Product ${fallbackId}`;
  }
  if (resource === 'order') {
    const orderNumber = typeof raw.order_number === 'string' ? raw.order_number.trim() : '';
    if (orderNumber) return `Order ${orderNumber}`;
    return `Order ${fallbackId}`;
  }
  if (resource === 'return') {
    const rma = typeof raw.rma === 'string' ? raw.rma.trim() : '';
    if (rma) return `Return ${fallbackId} (RMA ${rma})`;
    return `Return ${fallbackId}`;
  }
  if (resource === 'ship_advice') {
    const externalId = typeof raw.external_id === 'string' ? raw.external_id.trim() : '';
    if (externalId) return `Ship Advice ${externalId}`;
    return `Ship Advice ${fallbackId}`;
  }
  return `Fulfillment ${fallbackId}`;
}

function labelForResource(resource: PreviewEntityResource, id: number): string {
  switch (resource) {
    case 'catalog':
      return `Product ${id}`;
    case 'order':
      return `Order ${id}`;
    case 'return':
      return `Return ${id}`;
    case 'ship_advice':
      return `Ship Advice ${id}`;
    case 'fulfillment':
      return `Fulfillment ${id}`;
  }
}

function notFoundError(resource: PreviewEntityResource, id: number): EndpointResponse<ValidatedPreviewEntity> {
  return {
    isError: true,
    failureKind: 'failed',
    result: undefined,
    status: 404,
    execution_time: 0,
    num_results: 0,
    errors: { error: `${labelForResource(resource, id)} not found` },
  };
}

export async function validatePreviewEntity(
  scope: RequestScope,
  args: ValidatePreviewEntityArgs,
  options: ReadRequestOptions
): Promise<EndpointResponse<ValidatedPreviewEntity>> {
  const resource = getEntityResourceForContext(args.contextType);
  if (!resource) {
    return {
      isError: true,
      failureKind: 'failed',
      result: undefined,
      status: 0,
      execution_time: 0,
      num_results: 0,
      errors: { error: `Context type '${args.contextType}' does not accept an entity id` },
    };
  }
  const id = Number(args.entityId);
  if (!Number.isFinite(id) || !Number.isInteger(id) || id <= 0) {
    return {
      isError: true,
      failureKind: 'failed',
      result: undefined,
      status: 0,
      execution_time: 0,
      num_results: 0,
      errors: { error: 'Entity id must be a positive integer' },
    };
  }

  try {
    type EntityEnvelope = { result?: Record<string, unknown> | null; execution_time?: number };
    const response = await scopedApi(scope).get<EntityEnvelope>(`/${resource}/${id}`, options);
    const raw = response.data?.result;
    // Reject array payloads explicitly: `typeof [] === 'object'` would otherwise
    // pass arrays through and cause `deriveEntityLabel` to read undefined fields,
    // silently returning `Product ${id}` style fallbacks.
    const record = isPlainRecord(raw) ? raw : null;
    if (!record) {
      return notFoundError(resource, id);
    }
    return {
      result: {
        id,
        resource,
        label: deriveEntityLabel(resource, record, id),
      },
      status: response.status,
      execution_time: typeof response.data?.execution_time === 'number' ? response.data.execution_time : 0,
      num_results: 1,
    };
  } catch (e) {
    // Redact request data — the user-supplied entityId is part of the URL path
    // (and would already leak via `url`), but for symmetry with the other twig
    // endpoints we keep request-body/header metadata out of Bugsnag.
    return utils.error.handleAxiosError(e, { redactRequestData: true });
  }
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
